// NTA screens — pure helpers over bv_nta_result / bv_nta_line rows.

export type NtaResultStatus =
  | 'queued' | 'running' | 'calculated' | 'no_saving' | 'cannot_assess' | 'no_data' | 'approved' | 'issued'
  | 'accepted' | 'finalised' | 'failed';

export type NtaRun = {
  id: string;
  kind: 'adhoc' | 'bulk' | 'import';
  label: string | null;
  run_date: string;
  status: string;
  total: number;
  done: number;
  failed: number;
  created_at: string;
};

export type NtaResultRow = {
  id: string;
  run_id: string;
  site_id: string | null;
  nmi: string;
  source: 'engine' | 'energetica';
  market_region: string | null;
  projection_from: string | null;
  projection_to: string | null;
  source_from: string | null;
  source_to: string | null;
  baseline_code: string | null;
  best_code: string | null;
  baseline_total: number | null;
  best_total: number | null;
  savings: number | null;
  baseline_not_viable: boolean;
  status: NtaResultStatus;
  reasons: string[];
  error: string | null;
  issued_at: string | null;
  accepted_at: string | null;
  finalised_at: string | null;
  created_at: string;
  site: {
    id: string;
    nmi: string | null;
    addr_street: string | null;
    addr_city: string | null;
    client: { id: string; name: string | null } | null;
  } | null;
};

export type NtaLineRow = {
  country: string;
  market: string;
  market_region: string | null;
  service_point: string;
  scenario: string;
  eligible: boolean;
  ineligible_reason: string | null;
  period_from: string | null;
  period_to: string | null;
  product_class: string | null;
  product_type: string | null;
  product: string | null;
  component: string | null;
  component_type: string | null;
  component_class: string | null;
  quantity: number | null;
  quantity_unit: string | null;
  rate_unit: string | null;
  rate: number | null;
  adjustments: string | null;
  adjusted_rate_unit: string | null;
  adjusted_rate: number | null;
  amount: number;
  source_from: string | null;
  source_to: string | null;
  error: string | null;
};

export const NTA_STATUS_LABEL: Record<NtaResultStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  calculated: 'Saving found',
  no_saving: 'No saving',
  cannot_assess: 'Cannot assess',
  no_data: 'No data',
  approved: 'Approved',
  issued: 'Issued',
  // D-2: who accepts (retailer or distributor) is unconfirmed, so the labels name neither.
  accepted: 'Accepted',
  finalised: 'Finalised',
  failed: 'Failed',
};

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });
/** Postgres numeric arrives as a string through PostgREST. */
export const toNum = (v: number | string | null | undefined): number | null =>
  v == null || v === '' ? null : Number(v);
export const fmtMoney = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n == null ? '—' : AUD.format(n);
};

const r2 = (n: number) => Math.round(n * 100) / 100;

export type NtaScenario = {
  scenario: string;
  tariffCode: string;
  isBaseline: boolean;
  eligible: boolean;
  reason: string | null;
  total: number;
  lines: NtaLineRow[];
};

export const tariffCodeOf = (scenario: string): string => /\[([^\]]+)\]/.exec(scenario)?.[1] ?? scenario;

/** Lines -> scenarios, baseline first, then alternatives cheapest first. */
export function scenariosOf(lines: NtaLineRow[]): NtaScenario[] {
  const by = new Map<string, NtaLineRow[]>();
  for (const l of lines) {
    const list = by.get(l.scenario);
    if (list) list.push(l); else by.set(l.scenario, [l]);
  }
  return [...by].map(([scenario, ls]) => ({
    scenario,
    tariffCode: tariffCodeOf(scenario),
    isBaseline: scenario.startsWith('Baseline'),
    eligible: ls.every((l) => l.eligible),
    reason: ls.find((l) => l.ineligible_reason)?.ineligible_reason ?? null,
    total: r2(ls.reduce((s, l) => s + Number(l.amount), 0)),
    lines: ls,
  })).sort((a, b) => Number(b.isBaseline) - Number(a.isBaseline) || a.total - b.total);
}

export type NtaPivot = {
  months: string[];                       // period_from, ascending
  rows: Array<{ component: string; byMonth: Record<string, number>; total: number }>;
  monthTotals: Record<string, number>;
  total: number;
};

/** One scenario's lines as a component × month grid of amounts (row order = first appearance). */
export function pivotScenario(lines: NtaLineRow[]): NtaPivot {
  const months = [...new Set(lines.map((l) => l.period_from ?? ''))].filter(Boolean).sort();
  const rows = new Map<string, Record<string, number>>();
  const monthTotals: Record<string, number> = {};
  for (const l of lines) {
    const c = l.component ?? l.product ?? '—';
    const m = l.period_from ?? '';
    const row = rows.get(c) ?? {};
    row[m] = r2((row[m] ?? 0) + Number(l.amount));
    rows.set(c, row);
    monthTotals[m] = r2((monthTotals[m] ?? 0) + Number(l.amount));
  }
  const out = [...rows].map(([component, byMonth]) => ({
    component, byMonth, total: r2(Object.values(byMonth).reduce((s, v) => s + v, 0)),
  }));
  return { months, rows: out, monthTotals, total: r2(out.reduce((s, r) => s + r.total, 0)) };
}

export type NtaQuantityPivot = {
  months: string[];
  rows: Array<{ component: string; unit: string; byMonth: Record<string, number>; total: number }>;
  usageByMonth: Record<string, number>;   // all kWh components summed
  usageTotal: number;
};

const ENERGY_UNIT = 'kwh';
const DEMAND_UNITS = ['kw', 'kva'];

/** One scenario's usage (kWh) and demand (kW/kVA) quantities as a component × month grid.
 *  kWh rows total as a sum; demand rows total as the 12-month peak. */
export function pivotQuantities(lines: NtaLineRow[]): NtaQuantityPivot {
  const months = [...new Set(lines.map((l) => l.period_from ?? ''))].filter(Boolean).sort();
  const rows = new Map<string, { unit: string; byMonth: Record<string, number> }>();
  const usageByMonth: Record<string, number> = {};
  for (const l of lines) {
    const unit = (l.quantity_unit ?? '').trim();
    const u = unit.toLowerCase();
    // Expression rows (DPPC, Jurisdictional Scheme) re-price the Usage kWh; counting them would double it.
    if (l.quantity == null || l.component_type === 'Expression' || (u !== ENERGY_UNIT && !DEMAND_UNITS.includes(u))) continue;
    const c = l.component ?? l.product ?? '—';
    const m = l.period_from ?? '';
    const q = Number(l.quantity);
    const row = rows.get(c) ?? { unit, byMonth: {} };
    row.byMonth[m] = r2((row.byMonth[m] ?? 0) + q);
    rows.set(c, row);
    if (u === ENERGY_UNIT) usageByMonth[m] = r2((usageByMonth[m] ?? 0) + q);
  }
  const out = [...rows].map(([component, { unit, byMonth }]) => {
    const vals = Object.values(byMonth);
    const total = unit.toLowerCase() === ENERGY_UNIT ? r2(vals.reduce((s, v) => s + v, 0)) : Math.max(...vals);
    return { component, unit, byMonth, total };
  });
  return { months, rows: out, usageByMonth, usageTotal: r2(Object.values(usageByMonth).reduce((s, v) => s + v, 0)) };
}

/** The 26 Energetica headers, verbatim and in order (spec 8.2). */
export const NTA_CSV_HEADERS = [
  'Country', 'Market', 'Market Region', 'Service Point', 'Scenario', 'Eligible', 'Ineligible Reason',
  'From', 'To', 'Product Class', 'Product Type', 'Product', 'Component', 'Component Type',
  'Component Classification', 'Quantity', 'Quantity Unit', 'Rate Unit', 'Rate', 'Adjustments',
  'Adjusted Rate Unit', 'Adjusted Rate', 'Amount', 'Source Data From', 'Source Data To', 'Error',
];

const csvCell = (v: unknown): string => {
  if (v == null) return '';
  const s = typeof v === 'boolean' ? (v ? 'Y' : 'N') : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function linesToCsv(lines: NtaLineRow[]): string {
  const body = lines.map((l) => [
    l.country, l.market, l.market_region, l.service_point, l.scenario, l.eligible, l.ineligible_reason,
    l.period_from, l.period_to, l.product_class, l.product_type, l.product, l.component, l.component_type,
    l.component_class, l.quantity, l.quantity_unit, l.rate_unit, l.rate, l.adjustments, l.adjusted_rate_unit,
    l.adjusted_rate, l.amount, l.source_from, l.source_to, l.error,
  ].map(csvCell).join(','));
  return [NTA_CSV_HEADERS.join(','), ...body].join('\n');
}

/** Statuses whose saving counts toward the customer figure on the report. */
export const NTA_REPORTABLE: NtaResultStatus[] = ['calculated', 'approved', 'issued', 'accepted', 'finalised'];

/**
 * The single customer-level figure on the client report (spec 6.4): the sum of
 * positive savings across the customer's NMIs in one run. A site with no saving
 * contributes nothing rather than reducing the total.
 */
export function customerSavings(results: Array<Pick<NtaResultRow, 'savings' | 'status'>>): number {
  return r2(results
    .filter((r) => NTA_REPORTABLE.includes(r.status))
    .reduce((s, r) => s + Math.max(0, toNum(r.savings) ?? 0), 0));
}

export type TariffOpportunities = {
  /** Sites with any NTA result. */
  assessed: number;
  /** Sites whose latest result is a better tariff with a saving. */
  sites: number;
  approved: number;
  issued: number;
};

/**
 * The Dashboard's management figure: sites, never dollars. Each site counts
 * once, by its latest result, so re-running a site does not double it.
 */
export function tariffOpportunities(
  results: ReadonlyArray<Pick<NtaResultRow, 'site_id' | 'status' | 'savings' | 'created_at'>>,
): TariffOpportunities {
  const latest = new Map<string, Pick<NtaResultRow, 'status' | 'savings' | 'created_at'>>();
  for (const r of results) {
    if (!r.site_id) continue;
    const had = latest.get(r.site_id);
    if (!had || r.created_at > had.created_at) latest.set(r.site_id, r);
  }
  const out: TariffOpportunities = { assessed: latest.size, sites: 0, approved: 0, issued: 0 };
  for (const r of latest.values()) {
    if (!NTA_REPORTABLE.includes(r.status) || !((toNum(r.savings) ?? 0) > 0)) continue;
    out.sites += 1;
    if (r.status === 'approved') out.approved += 1;
    if (r.status === 'issued') out.issued += 1;
  }
  return out;
}

/** 'Mid 2025' for a Jan–Jun run, 'End 2025' for Jul–Dec — the Shield six-monthly cycle label. */
export function ntaPeriodLabel(runDate: string): string {
  const [y, m] = runDate.split('-').map(Number);
  return `${m <= 6 ? 'Mid' : 'End'} ${y}`;
}

/** Shield_NTA_<Period>_<AccountRef>_<CustomerName> (spec 8.1), filesystem-safe. */
export function ntaReportFilename(period: string, accountRef: string, customer: string): string {
  const safe = (s: string) => s.trim().replace(/[^\w\- ]+/g, '').replace(/\s+/g, '_');
  return ['Shield_NTA', safe(period), safe(accountRef), safe(customer)].filter(Boolean).join('_');
}
